import { useRef, useState } from 'react';
import {
  CATALOG_IMAGE_CONTENT_TYPES,
  CATALOG_IMAGE_MAX_BYTES,
  type CatalogMediaResult,
  type CatalogMediaUpload,
  type CatalogPart,
} from '@ddp/shared';

import { apiFetch } from '../../api/client.js';
import { useT, type MessageKey } from '../../i18n/index.js';

function isAllowedType(type: string): boolean {
  return (CATALOG_IMAGE_CONTENT_TYPES as readonly string[]).includes(type);
}

/**
 * Curation de l'illustration d'une pièce (ADR-0032), réservée au `SUPERADMIN`. Deux voies : import
 * depuis l'URL fabricant (recopie serveur) ou upload de fichier (dépôt pré-signé). `onDone` relit le
 * catalogue après une modification.
 */
export function CatalogMediaEditor({
  part,
  onClose,
  onDone,
}: {
  part: CatalogPart;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone();
    } catch (err) {
      const key = err instanceof Error ? err.message : '';
      setError(key.startsWith('hw.media.') ? t(key as MessageKey) : t('common.error'));
      setBusy(false);
    }
  };

  const doImport = () =>
    run(async () => {
      if (!url.trim()) throw new Error('hw.media.needUrl');
      await apiFetch<CatalogMediaResult>(`/catalog/${part.id}/media/import`, {
        method: 'POST',
        body: { sourceUrl: url.trim() },
      });
    });

  const doUpload = (file: File) =>
    run(async () => {
      if (!isAllowedType(file.type)) throw new Error('hw.media.badType');
      if (file.size > CATALOG_IMAGE_MAX_BYTES) throw new Error('hw.media.tooBig');
      const reserved = await apiFetch<CatalogMediaUpload>(`/catalog/${part.id}/media`, {
        method: 'POST',
        body: { contentType: file.type },
      });
      const put = await fetch(reserved.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!put.ok) throw new Error('hw.media.uploadFailed');
      await apiFetch<CatalogMediaResult>(`/catalog/${part.id}/media/confirm`, { method: 'POST' });
    });

  const doClear = () =>
    run(async () => {
      await apiFetch(`/catalog/${part.id}/media`, { method: 'DELETE' });
    });

  return (
    <div
      className="ddp-catmedia__overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('hw.media.title')}
    >
      <div className="ddp-catmedia__modal">
        <div className="ddp-catmedia__head">
          <span>
            {t('hw.media.title')} · <strong>{`${part.brand} ${part.model}`}</strong>
          </span>
          <button
            type="button"
            className="ddp-catmedia__x"
            onClick={onClose}
            aria-label={t('hw.media.close')}
          >
            ×
          </button>
        </div>

        {part.mediaUrl && <img className="ddp-catmedia__preview" src={part.mediaUrl} alt="" />}

        <label className="ddp-catmedia__label" htmlFor={`media-url-${part.id}`}>
          {t('hw.media.importLabel')}
        </label>
        <div className="ddp-catmedia__row">
          <input
            id={`media-url-${part.id}`}
            type="url"
            className="ddp-catmedia__input"
            placeholder={t('hw.media.importPlaceholder')}
            value={url}
            disabled={busy}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button
            type="button"
            className="ddp-catmedia__btn"
            disabled={busy}
            onClick={() => void doImport()}
          >
            {t('hw.media.import')}
          </button>
        </div>

        <div className="ddp-catmedia__or">{t('hw.media.or')}</div>

        <div className="ddp-catmedia__actions">
          <button
            type="button"
            className="ddp-catmedia__btn"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {t('hw.media.upload')}
          </button>
          {part.mediaUrl && (
            <button
              type="button"
              className="ddp-catmedia__btn ddp-catmedia__btn--danger"
              disabled={busy}
              onClick={() => void doClear()}
            >
              {t('hw.media.remove')}
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void doUpload(f);
            }}
          />
        </div>

        <p className="ddp-catmedia__hint">{busy ? t('hw.media.working') : t('hw.media.hint')}</p>
        {error && <p className="ddp-catmedia__error">{error}</p>}
      </div>
    </div>
  );
}
